import { useEffect, useState } from 'react';
import { useSyncStatus } from './useSyncStatus';
import { useSyncEngine } from './SyncProvider';
import { isStaleWriteFailure } from './staleWriteFailure';
import { describeFailedMutation } from './describeFailedMutation';
import { DiscardBusyError } from '../sync/SyncEngine';
import type { QueuedMutation } from '../queue/types';

/**
 * Where the failed-uploads panel opens relative to the badge:
 * - 'below-right' (default): below the badge, right edge anchored — for a
 *   badge near the top-right of the screen (e.g. an AppHeader's `right`
 *   slot), so the panel extends left and stays on-screen.
 * - 'above-right': above the badge, right edge anchored.
 * - 'above-left': above the badge, left edge anchored — for a badge fixed
 *   near the bottom-LEFT of the screen (opening below or to the right would
 *   run off-screen).
 */
export type SyncBadgePanelPlacement = 'below-right' | 'above-right' | 'above-left';

export interface SyncBadgeProps {
  /** Custom CSS class for the pending badge. */
  readonly className?: string;
  /** Custom CSS class for the failed badge. */
  readonly failedClassName?: string;
  /**
   * Confirmation gate for Discard, injected for tests. Defaults to
   * `window.confirm`. Must return true to proceed with the delete.
   */
  readonly confirmDiscard?: (message: string) => boolean;
  /** See SyncBadgePanelPlacement. Defaults to 'below-right'. */
  readonly panelPlacement?: SyncBadgePanelPlacement;
}

const DEFAULT_CLASS =
  'inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 cursor-pointer';

const DEFAULT_FAILED_CLASS =
  'inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 cursor-pointer';

const PANEL_PLACEMENT_CLASS: Record<SyncBadgePanelPlacement, string> = {
  'below-right': 'right-0 top-full mt-1',
  'above-right': 'right-0 bottom-full mb-1',
  'above-left': 'left-0 bottom-full mb-1',
};

type FailedEntry = { readonly mutation: QueuedMutation; readonly photoCount: number };

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Small badge showing pending and failed mutation counts.
 *
 * Tapping the pending count triggers a sync, unchanged. Tapping the failed
 * count no longer retries immediately — it opens a small panel listing each
 * failed item with what it actually contains (see describeFailedMutation),
 * a "Retry all" action, and a per-item Discard. Discard is the ONLY way to
 * free a failed mutation's photos (OFFICEHUB-SALES-8): nothing here ever
 * deletes on its own, and Discard always confirms first, naming exactly
 * what will be lost. The panel auto-closes the moment nothing is failed
 * anymore, and any handler error is shown inline rather than swallowed.
 */
export function SyncBadge({
  className = DEFAULT_CLASS,
  failedClassName = DEFAULT_FAILED_CLASS,
  confirmDiscard,
  panelPlacement = 'below-right',
}: SyncBadgeProps) {
  const engine = useSyncEngine();
  const { pendingCount, failedCount, isSyncing } = useSyncStatus(engine);
  const [showFailed, setShowFailed] = useState(false);
  const [failedEntries, setFailedEntries] = useState<readonly FailedEntry[]>([]);
  const [panelError, setPanelError] = useState<string | null>(null);

  // LOW-2: the panel must not linger open (with stale entries) once nothing
  // is failed anymore — e.g. a background retry (another tab, a scheduled
  // sync) clears the last failure while this panel happens to be open.
  useEffect(() => {
    if (failedCount === 0) setShowFailed(false);
  }, [failedCount]);

  if (pendingCount === 0 && failedCount === 0 && !isSyncing) return null;

  const handleClick = () => { void engine?.sync(); };

  const refreshFailedEntries = async () => {
    if (!engine) return;
    setFailedEntries(await engine.getFailedMutations());
  };

  const handleToggleFailed = async () => {
    if (showFailed) {
      setShowFailed(false);
      return;
    }
    setPanelError(null);
    try {
      await refreshFailedEntries();
      setShowFailed(true);
    } catch (err) {
      setPanelError(errorMessage(err, 'Could not load failed uploads.'));
      setShowFailed(true);
    }
  };

  const handleRetryAll = async () => {
    setPanelError(null);
    try {
      await engine?.retryFailed();
      setShowFailed(false);
    } catch (err) {
      setPanelError(errorMessage(err, 'Retry failed.'));
    }
  };

  const confirm = confirmDiscard ?? ((message: string) => window.confirm(message));

  const handleDiscard = async (mutation: QueuedMutation, photoCount: number) => {
    if (!engine) return;
    const ok = confirm(`${describeFailedMutation(mutation, photoCount)} Discard?`);
    if (!ok) return;
    setPanelError(null);
    try {
      await engine.discardFailed(mutation.id);
      await refreshFailedEntries();
    } catch (err) {
      // LOW-1: a sync running right now is a "try again" case, not the same
      // as a refusal (retainBlocks / already-gone) — worded distinctly so
      // the rep doesn't read it as a permanent failure.
      if (err instanceof DiscardBusyError) {
        setPanelError('Sync in progress, try again.');
      } else {
        setPanelError(errorMessage(err, 'Could not discard.'));
      }
    }
  };

  return (
    <span className="relative inline-flex gap-1">
      {(pendingCount > 0 || isSyncing) && (
        <button
          type="button"
          onClick={handleClick}
          className={className}
          aria-label={`${pendingCount} pending changes. Tap to sync.`}
        >
          {isSyncing ? (
            <span aria-hidden="true">&#8635;</span>
          ) : (
            <span>{pendingCount}</span>
          )}
          <span>{isSyncing ? 'Syncing...' : 'pending'}</span>
        </button>
      )}
      {failedCount > 0 && (
        <button
          type="button"
          onClick={handleToggleFailed}
          className={failedClassName}
          aria-label={`${failedCount} failed changes. Tap to review.`}
        >
          <span>{failedCount}</span>
          <span>failed</span>
        </button>
      )}
      {showFailed && (
        <div
          role="dialog"
          aria-label="Failed uploads"
          className={`absolute z-10 w-64 rounded-lg bg-white p-2 text-xs shadow-lg ring-1 ring-slate-200 ${PANEL_PLACEMENT_CLASS[panelPlacement]}`}
        >
          {/* A stale-write refusal can only be refused again. The list omits some failed
              rows (a retained photo upload), so compare against the badge's own count:
              hide Retry only when EVERY failure is a stale-write one we can see. */}
          {failedCount > failedEntries.filter(({ mutation }) => isStaleWriteFailure(mutation)).length && (
            <button
              type="button"
              onClick={handleRetryAll}
              className="mb-2 w-full rounded-md bg-brand-600 px-2 py-1.5 text-center font-semibold text-white"
            >
              Retry all
            </button>
          )}
          {panelError && (
            <p role="alert" className="mb-2 rounded bg-red-50 px-1.5 py-1 text-red-700">
              {panelError}
            </p>
          )}
          {failedEntries.length === 0 ? (
            <p className="px-1 py-1 text-slate-500">Nothing failed anymore.</p>
          ) : (
            // LOW-3: bounded height with its own scroll region — Retry-all
            // (above, outside this list) must stay reachable however many
            // failed items there are.
            <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
              {failedEntries.map(({ mutation, photoCount }) => (
                <li key={mutation.id} className="flex items-center justify-between gap-2 rounded px-1 py-1">
                  <span className="text-slate-700">
                    {/* LOW-2: the full description (not a bare photo count) —
                        a notes-only item has 0 photos and "0 photos" reads as
                        a bug, not as "there's still content here". */}
                    {describeFailedMutation(mutation, photoCount)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDiscard(mutation, photoCount)}
                    className="font-semibold text-red-700"
                  >
                    Discard
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}
